import { motion } from "framer-motion";
import Reveal, { stagger, fadeUp } from "@/components/Reveal";
import { Check, ArrowRight } from "lucide-react";
import { scrollToId } from "@/App";

const SERVICES = [
  {
    id: "commercial-growth",
    tag: "01 / B2B Strategy & Outreach",
    title: "Commercial Growth",
    desc: "We open doors and build strategic relationships across industries — from SMEs to corporate and government — turning conversations into long-term, high-value customers.",
    points: [
      "Targeted executive & B2B relationship building",
      "SME to corporate & government door-opening",
      "Strategic partnership development",
      "Long-term customer acquisition strategy",
    ],
    stat: "Conversations into long-term customers",
    img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=90",
    alt: "Two professionals shaking hands on a commercial deal",
  },
  {
    id: "real-estate-support",
    tag: "02 / Outsourced Prospecting & PA",
    title: "Real Estate Support",
    desc: "We provide real people for prospecting, cold calling, follow-ups, PA tasks and on-the-ground support — taking the pressure off and keeping your pipeline moving.",
    points: [
      "Dedicated cold calling & lead qualification",
      "Database nurturing & systematic follow-ups",
      "Executive PA & admin workflow support",
      "On-the-ground listing & appraisal support",
    ],
    stat: "Your pipeline never sits still",
    img: "https://images.unsplash.com/photo-1537511446984-935f663eb1f4?auto=format&fit=crop&w=1400&q=90",
    alt: "Real estate professional making prospecting calls",
  },
  {
    id: "sales-training",
    tag: "03 / Conversion Coaching",
    title: "Sales Training",
    desc: "Targeted sales training designed to sharpen your team's skills, effectively navigate conversations, and close more deals.",
    points: [
      "Practical objection handling & closing frameworks",
      "Role-playing high-stakes conversations",
      "Scripts tuned for NZ & Australian markets",
      "Ongoing performance coaching",
    ],
    stat: "Sharper conversations. More closed deals.",
    img: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1400&q=90",
    alt: "Sales team training workshop",
  },
  {
    id: "marketing-content",
    tag: "04 / Digital Brand & Reach",
    title: "Marketing & Content",
    desc: "From social media management and content creation to digital marketing — we make sure your business gets seen and stays top of mind with the right audience.",
    points: [
      "Social media management & content calendars",
      "Digital advertising & retargeting campaigns",
      "High-converting content production",
      "Brand positioning & audience engagement",
    ],
    stat: "Get seen. Stay top of mind.",
    img: "https://images.unsplash.com/photo-1582005450386-52b25f82d9bb?auto=format&fit=crop&w=1400&q=90",
    alt: "Digital marketing team planning content strategy",
  },
  {
    id: "business-growth",
    tag: "05 / Auditing & Scaling",
    title: "Business Growth & Strategy",
    desc: "Through comprehensive business auditing, we identify operational gaps, develop targeted strategies, explore new partnerships and expand your customer base.",
    points: [
      "Full operational & revenue gap auditing",
      "Customised growth roadmaps & KPIs",
      "New partnership discovery",
      "Scalable expansion tactics for NZ & AU",
    ],
    stat: "Gaps found. Strategy sorted. Growth unlocked.",
    img: "https://images.unsplash.com/photo-1532619675605-1ede6c2ed2b0?auto=format&fit=crop&w=1400&q=90",
    alt: "Business strategy planning session at a whiteboard",
  },
];

export default function ServiceRows() {
  return (
    <section id="services" data-testid="services-section" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200/60 inline-block">
              Our Services
            </span>
          </Reveal>
          <Reveal>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
              One partner for every part of your growth
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              Five hands-on services that take the pressure off your team and keep
              revenue opportunities flowing.
            </p>
          </Reveal>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="space-y-8 sm:space-y-12"
        >
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.id}
              variants={fadeUp}
              data-testid={`service-row-${s.id}`}
              className="grid grid-cols-1 lg:grid-cols-2 rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-xl hover:shadow-slate-900/5 hover:border-emerald-200 transition-all duration-500"
            >
              <div className={`relative h-64 sm:h-80 lg:h-auto ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img src={s.img} alt={s.alt} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="p-8 sm:p-12 flex flex-col justify-center">
                <p className="font-mono-accent text-[11px] font-medium uppercase tracking-[0.2em] text-emerald-600">
                  {s.tag}
                </p>
                <h3 className="font-display mt-3 text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-base text-slate-600 leading-relaxed">{s.desc}</p>
                <ul className="mt-6 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-slate-700">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-emerald-600" />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    data-testid={`service-enquire-${s.id}`}
                    onClick={() => scrollToId("contact")}
                    className="group inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
                  >
                    Enquire about this
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <span className="text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5">
                    {s.stat}
                  </span>
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
